import { Component, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../core/services/user.service';
import { AuthResponse } from '../../core/interfaces/api-response';
import { emailValidator } from '../../core/validators/email.validator';
import { DialogService } from "../../core/services/dialog.service";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  standalone: false
})

/**
 * Login Component with Remember Me functionality
 */
export class LoginComponent implements OnInit {
  year: number = new Date().getFullYear();
  submitted = false;
  loading = false;
  returnUrl!: string;
  error = '';
  hide = true;
  loginForm!: UntypedFormGroup;
  showSessionExpired = false;

  private translate = inject(TranslateService);

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      name: ['', [Validators.required, emailValidator()]],
      password: ['', [Validators.required]],
      rememberMe: [false]
    });

    // Check for session expiry or other redirect reasons
    this.route.queryParams.subscribe(params => {
      this.returnUrl = this.sanitizeReturnUrl(params['returnUrl']);

      if (params['reason'] === 'session_expired') {
        this.showSessionExpired = true;
      }
      if (params['reason'] === 'two_factor_expired') {
        this.error = this.translate.instant('AUTH.VERIFY_2FA.TEMP_TOKEN_EXPIRED');
      }
    });
  }

  /**
   * For hotel users (extranet users / hotel managers), clear the saved hotel selection
   * so the hotel search screen is always shown after login instead of auto-redirecting
   * to the last used hotel.
   * Internal HotelCard staff are not affected.
   */
  private clearSavedHotelForHotelUser(): void {
    const user = this.userService.getUser();
    if (!user?.id || user.is_internal) {
      return;
    }
    localStorage.removeItem(`hc_selected_hotel_${user.id}`);
    localStorage.removeItem('lastHotelId');
  }

  /**
   * Sanitize returnUrl to prevent redirect loops.
   * Rejects login page URLs and external URLs.
   */
  private sanitizeReturnUrl(url: string | undefined): string {
    if (!url || url.includes('/account/login') || url.includes('/account/verify-2fa')) {
      return '/dashboard';
    }
    return url;
  }

  // convenience getter for easy access to form fields
  get f(): { [key: string]: AbstractControl } {
    return this.loginForm.controls;
  }

  /**
   * Form submit with Remember Me support
   */
  async onSubmit(): Promise<void> {
    if (this.loading) {
      return;
    }

    this.submitted = true;
    this.loading = true;  // Set loading state
    this.error = '';

    // Mark all controls as touched to trigger validation display
    this.loginForm.markAllAsTouched();

    // stop here if the form is invalid
    if (this.loginForm.invalid) {
      this.loading = false;
      return;
    }

    const user = this.userService.getEmptyLoginForm();
    user.password = this.f['password'].value;
    user.username = this.f['name'].value;

    // Get Remember Me value
    const rememberMe = this.f['rememberMe'].value;
    this.userService.clearPendingTwoFactorState();

    try {
      // Call the async login method with Remember Me
      const loginObservable = await this.userService.login(user, rememberMe);

      loginObservable.subscribe({
        next: (res) => {
          this.loading = false;
          const authResponse = res as AuthResponse;

          // Check if 2FA was skipped (trusted device)
          const skipped2fa = authResponse?.data?.skipped_2fa || authResponse?.skipped_2fa;
          if (skipped2fa) {
            // Trusted device — 2FA was skipped, proceed with login
          }

          // 2FA required — temp_token already stored by UserService, redirect to verify page
          const requires2fa = authResponse?.data?.requires_2fa || authResponse?.requires_2fa;
          if (requires2fa) {
            if (!this.userService.getTempToken()) {
              this.error = this.translate.instant('AUTH.LOGIN.LOGIN_UNAVAILABLE');
              return;
            }
            this.router.navigate(['/account/verify-2fa'], {
              queryParams: { returnUrl: this.returnUrl }
            });
            return;
          }

          // The access token is the definitive success indicator
          const token = this.userService.getToken();
          if (token) {
            this.showSessionExpired = false;
            this.clearSavedHotelForHotelUser();
            this.router.navigateByUrl(this.returnUrl || '/dashboard');
          } else {
            this.error = this.translate.instant('AUTH.LOGIN.INVALID_LOGIN');
          }
        },
        error: (error: unknown) => {
          this.error = this.resolveErrorMessage(error);
          this.submitted = false;
          this.loading = false;
        },
      });
    } catch (error) {
      this.error = this.resolveErrorMessage(error);
      this.submitted = false;
      this.loading = false;
    }
  }

  openCardValidityDialog(): void {
    this.dialogService.openCardValidityDialog();
  }

  private getErrorStatus(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null) return undefined;
    const status = (error as { status?: unknown }).status;
    return typeof status === 'number' ? status : undefined;
  }

  /**
   * Prefer the backend's specific reason (e.g. "No account found with this email
   * address." vs "The password you entered is incorrect.") over a generic message,
   * so the same "credentials incorrect" text isn't shown for unrelated failures.
   * Only trusted for responses that actually reached the server (status > 0) —
   * a status-less/0 error means the interceptor's own generic fallback text
   * ("Unknown Error" etc.), not a real backend message, so that case still
   * falls back to the translated copy below.
   */
  private resolveErrorMessage(error: unknown): string {
    const status = this.getErrorStatus(error);
    const backendMessage = status ? this.getBackendMessage(error) : undefined;
    if (backendMessage) {
      return backendMessage;
    }

    return this.translate.instant(
      status === 401 ? 'AUTH.LOGIN.INVALID_LOGIN' : 'AUTH.LOGIN.LOGIN_UNAVAILABLE'
    );
  }

  private getBackendMessage(error: unknown): string | undefined {
    if (typeof error !== 'object' || error === null) return undefined;
    const message = (error as { message?: unknown }).message;
    return typeof message === 'string' && message.trim().length > 0 ? message : undefined;
  }
}
